import { canWriteEntity, logger } from '@tunasong/models'
import type { TunaThunkDispatch } from '@tunasong/redux'
import { features } from '@tunasong/redux'
import type { Entity, Persisted } from '@tunasong/schemas'

interface MoveArgs {
  entity: Persisted<Entity>
  newParent: Persisted<Entity>
  /** The signed-in user, so the refusal can name the side they cannot write to */
  userId?: string | null
}

/** Name the side the user cannot write to when we can tell; otherwise name both (GH #2155, Joni's copy) */
const refusedMessage = ({ entity, newParent, userId }: MoveArgs) => {
  const name = entity.name ?? 'Untitled'
  const destination = newParent.name ?? 'Untitled'
  const canWriteItem = canWriteEntity(userId, entity)
  const canWriteDestination = canWriteEntity(userId, newParent)
  if (userId && canWriteItem && !canWriteDestination) {
    return `Nothing changed. You can't add items to "${destination}". Ask its owner for edit access.`
  }
  if (userId && !canWriteItem && canWriteDestination) {
    return `Nothing changed. You can't move "${name}". Ask its owner for edit access.`
  }
  return `Nothing changed. Moving needs edit access to "${name}" and to "${destination}".`
}

/**
 * Move an entity under `parentId`, or to the top level (Unfiled) when it is `null`, without any alert: the caller
 * says the outcome in its own words (the Move picker, #2543). Resolves `true` when the move happened.
 */
export const moveEntityQuietly = async (
  dispatch: TunaThunkDispatch,
  { entityId, parentId }: { entityId: string; parentId: string | null }
): Promise<boolean> => {
  const result = await dispatch(features.entities.thunks.addChild({ parentId: parentId ?? 'ROOT', childId: entityId }))
  if (!features.entities.thunks.addChild.rejected.match(result)) {
    return true
  }
  logger.warn('Move failed', { entityId, parentId, error: result.error })
  return false
}

/**
 * Move `entity` under `newParent` and tell the user when it did not happen (GH #2155). A dispatched thunk resolves
 * even when it throws, so a move the user may not make (no write access to the item or to the target) used to
 * close the confirm dialog with nothing moved and nothing said. Resolves `true` when the move happened.
 */
export const moveEntityWithFeedback = async (dispatch: TunaThunkDispatch, args: MoveArgs): Promise<boolean> => {
  const { entity, newParent } = args
  const result = await dispatch(features.entities.thunks.addChild({ parentId: newParent.id, childId: entity.id }))
  if (!features.entities.thunks.addChild.rejected.match(result)) {
    return true
  }
  logger.warn('Move failed', { entityId: entity.id, parentId: newParent.id, error: result.error })
  dispatch(
    features.notifications.actions.setAlert({
      severity: 'error',
      title: `Couldn't move "${entity.name ?? 'Untitled'}"`,
      message: refusedMessage(args),
    })
  )
  return false
}
